/**
 * Topics hooks for React Query
 *
 * Provides data fetching and mutation hooks for topic management
 * and task-topic relationships.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as topicsApi from '@/api/topics';

// Query keys
export const topicKeys = {
  all: ['topics'] as const,
  lists: () => [...topicKeys.all, 'list'] as const,
  list: (options?: { type?: string; includeArchived?: boolean }) =>
    [...topicKeys.lists(), options] as const,
  detail: (id: string) => [...topicKeys.all, 'detail', id] as const,
  tree: () => [...topicKeys.all, 'tree'] as const,
  supportingTasks: (topicId: string) => [...topicKeys.all, 'supportingTasks', topicId] as const,
};

// Query keys for task-topic relationships (under tasks namespace)
export const taskTopicKeys = {
  relatedTopics: (taskId: string) => ['tasks', 'relatedTopics', taskId] as const,
};

// =============================================================================
// TOPICS QUERIES
// =============================================================================

/**
 * Hook for listing topics
 */
export function useTopics(options?: {
  type?: string;
  includeArchived?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: topicKeys.list(options),
    queryFn: () => topicsApi.listTopics(options),
    staleTime: 5 * 60 * 1000, // 5 minutes - topics change less frequently
  });
}

/**
 * Hook for getting a single topic
 */
export function useTopic(topicId: string) {
  return useQuery({
    queryKey: topicKeys.detail(topicId),
    queryFn: () => topicsApi.getTopic(topicId),
    enabled: !!topicId,
  });
}

/**
 * Hook for getting topic tree
 */
export function useTopicTree() {
  return useQuery({
    queryKey: topicKeys.tree(),
    queryFn: () => topicsApi.getTopicTree(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for getting supporting tasks for a topic
 */
export function useSupportingTasks(topicId: string) {
  return useQuery({
    queryKey: topicKeys.supportingTasks(topicId),
    queryFn: () => topicsApi.getSupportingTasks(topicId),
    enabled: !!topicId,
  });
}

// =============================================================================
// TASK-TOPIC RELATIONSHIP QUERIES
// =============================================================================

/**
 * Hook for getting topics that a task is related to
 */
export function useTaskRelatedTopics(taskId: string) {
  return useQuery({
    queryKey: taskTopicKeys.relatedTopics(taskId),
    queryFn: () => topicsApi.getTaskRelatedTopics(taskId),
    enabled: !!taskId,
  });
}

// =============================================================================
// TOPIC MUTATIONS
// =============================================================================

/**
 * Hook for topic mutations (create, update, delete, archive)
 */
export function useTopicMutations() {
  const queryClient = useQueryClient();

  // Invalidate all topic queries
  const invalidateTopics = () => {
    queryClient.invalidateQueries({ queryKey: topicKeys.all });
  };

  // Create topic
  const createTopic = useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      topicType?: string;
      color?: string;
      icon?: string;
      parentId?: string;
    }) => topicsApi.createTopic(input),
    onSuccess: () => {
      invalidateTopics();
    },
  });

  // Update topic
  const updateTopic = useMutation({
    mutationFn: ({
      topicId,
      input,
    }: {
      topicId: string;
      input: Partial<{
        name: string;
        description: string;
        color: string;
        icon: string;
        topicType: string;
      }>;
    }) => topicsApi.updateTopic(topicId, input),
    onSuccess: () => {
      invalidateTopics();
    },
  });

  // Delete topic
  const deleteTopic = useMutation({
    mutationFn: (topicId: string) => topicsApi.deleteTopic(topicId),
    onSuccess: () => {
      invalidateTopics();
    },
  });

  // Archive topic
  const archiveTopic = useMutation({
    mutationFn: (topicId: string) => topicsApi.archiveTopic(topicId),
    onSuccess: () => {
      invalidateTopics();
    },
  });

  // Unarchive topic
  const unarchiveTopic = useMutation({
    mutationFn: (topicId: string) => topicsApi.unarchiveTopic(topicId),
    onSuccess: () => {
      invalidateTopics();
    },
  });

  return {
    createTopic,
    updateTopic,
    deleteTopic,
    archiveTopic,
    unarchiveTopic,
  };
}

// =============================================================================
// TASK-TOPIC RELATIONSHIP MUTATIONS
// =============================================================================

/**
 * Hook for task-topic relationship mutations
 */
export function useTaskTopicMutations() {
  const queryClient = useQueryClient();

  // Set related topics for a task
  const setTaskRelatedTopics = useMutation({
    mutationFn: ({
      taskId,
      topicIds,
      source = 'manual',
    }: {
      taskId: string;
      topicIds: string[];
      source?: string;
    }) => topicsApi.setTaskRelatedTopics(taskId, topicIds, source),
    onSuccess: (_, { taskId }) => {
      // Invalidate task's related topics
      queryClient.invalidateQueries({ queryKey: taskTopicKeys.relatedTopics(taskId) });
      // Invalidate supporting tasks for all topics (we don't know which were affected)
      queryClient.invalidateQueries({ queryKey: topicKeys.all });
      // Invalidate task queries (in case we want to show topics on task cards)
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  // Remove a single task-topic relation
  const removeTaskTopicRelation = useMutation({
    mutationFn: ({ taskId, topicId }: { taskId: string; topicId: string }) =>
      topicsApi.removeTaskTopicRelation(taskId, topicId),
    onSuccess: (_, { taskId, topicId }) => {
      // Invalidate specific queries
      queryClient.invalidateQueries({ queryKey: taskTopicKeys.relatedTopics(taskId) });
      queryClient.invalidateQueries({ queryKey: topicKeys.supportingTasks(topicId) });
    },
  });

  // Remove a supporting task from a topic (same underlying operation, different perspective)
  const removeSupportingTask = useMutation({
    mutationFn: ({ topicId, taskId }: { topicId: string; taskId: string }) =>
      topicsApi.removeSupportingTask(topicId, taskId),
    onSuccess: (_, { topicId, taskId }) => {
      // Invalidate specific queries
      queryClient.invalidateQueries({ queryKey: topicKeys.supportingTasks(topicId) });
      queryClient.invalidateQueries({ queryKey: taskTopicKeys.relatedTopics(taskId) });
    },
  });

  return {
    setTaskRelatedTopics,
    removeTaskTopicRelation,
    removeSupportingTask,
  };
}

